/**
 * The hover card of one hour on the Day timeline's chart: the hour in Danish
 * time (the country's under it), then the values first — drops and SMS — and
 * each container's share keyed by a short line in its colour, the lists the
 * hour sends to, and "window closed" when it is.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { instantDk } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';
import type { HourBucket } from './day-hours';

const HOUR = 3_600_000;
const n = (v: number) => v.toLocaleString('en-US');
const plural = (v: number, word: string) => `${n(v)} ${word}${v === 1 ? '' : 's'}`;
const localClock = (iso: string, tz: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz });

export const HourTip: FC<{ hour: HourBucket }> = ({ hour }) => {
  const { tz, flag } = useCountryClock();
  const endIso = new Date(Date.parse(hour.startIso) + HOUR).toISOString();
  return (
    <span className="flex min-w-[210px] flex-col gap-1.5 text-left text-xs">
      <span className="flex items-baseline justify-between gap-3">
        <span className="font-mono font-semibold">{instantDk(hour.startIso).label}–{instantDk(endIso).label} 🇩🇰</span>
        {tz ? <span className="font-mono text-[10.5px] opacity-70">{flag} {localClock(hour.startIso, tz)}–{localClock(endIso, tz)}</span> : null}
      </span>
      {hour.drops > 0 ? (
        <>
          <span className="text-sm font-semibold">
            {plural(hour.drops, 'drop')} · {n(hour.sms)} SMS
          </span>
          {hour.slices.map((s) => (
            <span key={s.containerId} className="flex items-center gap-2">
              <span className="h-[2px] w-3 shrink-0 rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
              <span className="min-w-0 flex-1 truncate opacity-80">{s.name}</span>
              <span className="font-mono tabular-nums">{plural(s.drops, 'drop')} · {n(s.sms)} SMS</span>
            </span>
          ))}
          <span className="truncate font-mono text-[10.5px] opacity-70" title={hour.lists.join(', ')}>
            {hour.lists.slice(0, 3).join(' · ')}
            {hour.lists.length > 3 ? ` +${hour.lists.length - 3}` : ''}
          </span>
        </>
      ) : (
        <span className="opacity-70">No drop in this hour</span>
      )}
      {!hour.open ? <span className="text-[10.5px] opacity-70">Window closed</span> : null}
    </span>
  );
};
